import React, { useCallback, useEffect, useState } from 'react';
import { List, useListContext } from 'react-admin';
import { useMediaQuery, Theme, Stack, Tabs, Tab, Box } from '@mui/material';
import FuneralListActions from './FuneralListToolBar';
import { CremationStatus } from '../types';
import MobileFuneralList from './MobileFuneralList';
import { EnrichedFuneralGroup, isClosed } from './EnrichedFuneralGroup';
import { FuneralDrawerProvider } from './FuneralDrawerContext';
import { PaperworkNotificationProvider } from './PaperworkNotificationContext';
import { KewixTransportStrip } from '../answering-service/KewixTransportStrip';

// Delivered is split into two groups on the same fetch: still-open cases
// (Delivered, open by default) and Closed (delivered + all paperwork green,
// collapsed by default).
const statusConfig: Array<{
  key: string;
  status: CremationStatus;
  label: string;
  color: string;
  collapsed?: boolean;
  rowFilter?: (r: any) => boolean;
}> = [
  { key: 'arrangement', status: CremationStatus.ARRANGEMENT, label: 'Arrangement', color: '#f5f5f5' },
  { key: 'transport', status: CremationStatus.TRANSPORT_DISPATCHED, label: 'Transport Dispatched', color: '#fff8e1' },
  { key: 'cremation', status: CremationStatus.CREMATION_SCHEDULED, label: 'Cremation Scheduled', color: '#e3f2fd' },
  { key: 'ready', status: CremationStatus.READY_FOR_DELIVERY, label: 'Ready for Delivery', color: '#ede7f6' },
  { key: 'delivery', status: CremationStatus.DELIVERY_SCHEDULED, label: 'Delivery Scheduled', color: '#fce4ec' },
  { key: 'delivered', status: CremationStatus.DELIVERED, label: 'Delivered', color: '#e8f5e9', rowFilter: (r) => !isClosed(r) },
  { key: 'closed', status: CremationStatus.DELIVERED, label: 'Closed', color: '#eceff1', collapsed: true, rowFilter: isClosed },
];

export const FuneralsList = () => {
  const isXSmall = useMediaQuery<Theme>(theme =>
    theme.breakpoints.down('sm')
  );

  return (
    <List
      actions={false}
      // No `filters` prop: react-admin renders a ListToolbar for `filters ||
      // actions`, and with `actions={false}` that toolbar held nothing but an
      // empty FilterForm — a blank ~64px band above the tabs. The inputs it
      // could have shown were unreachable regardless, since dropping the
      // toolbar's Filter button left no way to display them; date ranges live
      // in the filter popover now, and search is in the toolbar.
      filterDefaultValues={{ 'archived_at@is': 'null', 'has_preneed@neq': true }}
      storeKey="funerals-v2"
      // Desktop renders the self-fetching status groups, which ignore the
      // List's own query — its pagination would page an invisible result set.
      // Mobile (MobileFuneralList) reads the List query, so it keeps paging.
      pagination={isXSmall ? undefined : false}
      perPage={50}
      sort={{ field: 'id', order: 'DESC' }}
      // The status groups carry their own cards; drop the outer List content
      // card's background/border/shadow so they sit on the page background.
      sx={{
        '& .RaList-content': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none',
        },
      }}
    >
      <TabbedGroupedView />
    </List>
  );
};

/** At-Need / Pre-Need tabs at the top, cremation-status groups below */
const TabbedGroupedView = () => {
  const { filterValues, setFilters, displayedFilters, sort } = useListContext();
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

  const [currentTab, setCurrentTab] = useState('atneed');

  useEffect(() => {
    if (filterValues['has_preneed@eq'] === true) {
      setCurrentTab('preneed');
    } else {
      setCurrentTab('atneed');
    }
  }, [filterValues]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);

    const newFilters = { ...filterValues };

    delete newFilters['region@eq'];
    delete newFilters['region@neq'];
    delete newFilters['has_preneed@eq'];
    delete newFilters['has_preneed@neq'];
    delete newFilters['has_atneed@eq'];

    if (newValue === 'atneed') {
      newFilters['has_preneed@neq'] = true;
    } else if (newValue === 'preneed') {
      newFilters['has_preneed@eq'] = true;
    }

    setFilters(newFilters, displayedFilters);
  }, [filterValues, setFilters, displayedFilters]);

  if (isXSmall)
    return (
      <FuneralDrawerProvider>
        <PaperworkNotificationProvider>
          <KewixTransportStrip />
          <MobileFuneralList />
        </PaperworkNotificationProvider>
      </FuneralDrawerProvider>
    );

  // Build base filters for the status groups (strip cremation_status to avoid conflicts)
  const baseFilters = { ...filterValues };
  delete baseFilters.cremation_status;
  delete baseFilters['cremation_status@neq'];

  return (
    <FuneralDrawerProvider>
      <PaperworkNotificationProvider>
        {/* Tabs choose which book you're looking at; the toolbar below narrows
            what's in it. Stacking them keeps that distinction legible, and
            gives the toolbar the full width it needs now that search and the
            filter set have moved into it. */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="At-Need" value="atneed" />
            <Tab label="Pre-Need" value="preneed" />
          </Tabs>
        </Box>
        <FuneralListActions />
        {/* Hidden unless Kewix is holding a death in its transport queue (T-150). */}
        <KewixTransportStrip />
        <Stack spacing={2} sx={{ mt: 2, pb: 2 }}>
          {statusConfig.map((cfg) => (
            <EnrichedFuneralGroup
              key={cfg.key}
              status={cfg.status}
              label={cfg.label}
              color={cfg.color}
              defaultExpanded={!cfg.collapsed}
              rowFilter={cfg.rowFilter}
              baseFilters={baseFilters}
              sort={sort}
            />
          ))}
        </Stack>
      </PaperworkNotificationProvider>
    </FuneralDrawerProvider>
  );
};


